import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { useAuth } from "../contexts/AuthContext";

interface Props {
  tripId: string;
}

interface TaskWithParticipants {
  id: string;
  title: string | null;
  description?: string | null;
  is_optional: boolean | null;
  phase?: string | null;
  due_date?: string | null;
  assigned_participants: {
    user_id: string;
    full_name: string;
    done: boolean;
  }[];
}

export default function TodoStatusPanel({ tripId }: Props) {
  const [tasks, setTasks] = useState<TaskWithParticipants[]>([]);
  const [loading, setLoading] = useState(true);

  const [filterOnlyOpen, setFilterOnlyOpen] = useState(false);
  const [filterOnlyRequired, setFilterOnlyRequired] = useState(false);
  const [filterPhase, setFilterPhase] = useState<string[]>([]);
  const [viewByUser, setViewByUser] = useState(false);

  const { user } = useAuth();

  useEffect(() => {
    if (!tripId) return;
    loadTaskStatus();
  }, [tripId]);

  const loadTaskStatus = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("trip_todos_with_assignments")
      .select(
        `
        todo_id,
        trip_id,
        title,
        description,
        is_optional,
        phase,
        due_date,
        user_id,
        status,
        first_name,
        last_name
      `
      )
      .eq("trip_id", tripId);

    if (error) {
      console.error("❌ Fehler beim Laden der Aufgaben:", error.message);
    } else {
      const grouped = data.reduce<Record<string, TaskWithParticipants>>(
        (acc, row) => {
          if (!row.todo_id) return acc;
          if (!acc[row.todo_id]) {
            acc[row.todo_id] = {
              id: row.todo_id,
              title: row.title,
              description: row.description,
              phase: row.phase,
              due_date: row.due_date,
              is_optional: row.is_optional,
              assigned_participants: [],
            };
          }

          if (row.user_id) {
            const fullName = `${row.first_name || ""} ${
              row.last_name || ""
            }`.trim();
            acc[row.todo_id].assigned_participants.push({
              user_id: row.user_id,
              full_name: fullName || row.user_id,
              done: row.status === "done",
            });
          }

          return acc;
        },
        {}
      );
      setTasks(Object.values(grouped));
    }

    setLoading(false);
  };

  const renderPhase = (phase?: string | null) => {
    switch (phase) {
      case "before":
        return "⏰ vor der Reise";
      case "during":
        return "🌞 während der Reise";
      case "after":
        return "📦 nach der Reise";
      default:
        return null;
    }
  };

  const filteredTasks = tasks.filter((task) => {
    if (filterOnlyOpen && task.assigned_participants.every((p) => p.done))
      return false;
    if (filterOnlyRequired && task.is_optional) return false;
    if (
      filterPhase.length > 0 &&
      (!task.phase || !filterPhase.includes(task.phase))
    )
      return false;
    return true;
  });

  const phaseSortOrder = { before: 0, during: 1, after: 2, none: 3 };

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const phaseA = (a.phase ?? "none") as keyof typeof phaseSortOrder;
    const phaseB = (b.phase ?? "none") as keyof typeof phaseSortOrder;

    if (phaseSortOrder[phaseA] !== phaseSortOrder[phaseB]) {
      return phaseSortOrder[phaseA] - phaseSortOrder[phaseB];
    }

    const dateA = a.due_date ? new Date(a.due_date).getTime() : Infinity;
    const dateB = b.due_date ? new Date(b.due_date).getTime() : Infinity;

    if (dateA !== dateB) return dateA - dateB;

    return (a.title ?? "").localeCompare(b.title ?? "");
  });

  const groupedTasks: Record<string, TaskWithParticipants[]> =
    sortedTasks.reduce((acc, task) => {
      const key = task.phase ?? "none";
      if (!acc[key]) acc[key] = [];
      acc[key].push(task);
      return acc;
    }, {} as Record<string, TaskWithParticipants[]>);

  const phaseLabels: Record<string, string> = {
    before: "🕒 vor der Reise",
    during: "🧭 während der Reise",
    after: "📦 nach der Reise",
    none: "🔸 ohne Phase",
  };

  const phaseOrder = ["before", "during", "after", "none"];

  const isOrganizer = (userId: string) =>
    tasks.some((t) =>
      t.assigned_participants.some((p) => p.user_id === userId)
    ) && true;

  const toggleDone = async (todoId: string, userId: string, done: boolean) => {
    const { error } = await supabase
      .from("trip_todo_assignments")
      .update({ status: done ? "done" : "pending" })
      .eq("todo_id", todoId)
      .eq("user_id", userId);

    if (error) {
      console.error("❌ Fehler beim Aktualisieren des Status:", error.message);
    } else {
      loadTaskStatus();
    }
  };

  const tasksPerUser = useMemo(() => {
    const map: Record<
      string,
      {
        full_name: string;
        items: { task: TaskWithParticipants; done: boolean }[];
      }
    > = {};

    for (const task of filteredTasks) {
      for (const participant of task.assigned_participants) {
        if (!map[participant.user_id]) {
          map[participant.user_id] = {
            full_name: participant.full_name,
            items: [],
          };
        }

        map[participant.user_id].items.push({ task, done: participant.done });
      }
    }

    return Object.entries(map).sort((a, b) =>
      a[1].full_name.localeCompare(b[1].full_name)
    );
  }, [filteredTasks]);

  /** Unified filter chip */
  const FilterChip = ({
    label,
    active,
    onClick,
  }: {
    label: string;
    active: boolean;
    onClick: () => void;
  }) => (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
        active
          ? "bg-sky-500/20 text-sky-400 border border-sky-500/30"
          : "bg-[#1e2639] text-slate-400 hover:text-slate-200"
      }`}
    >
      {label}
      {active && <span className="opacity-50 ml-0.5">✕</span>}
    </button>
  );

  return (
    <div className="mt-4">
      <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-5 px-1">
        Aufgabenstatus
      </h3>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-5">
        <FilterChip
          label="👥 Gruppiert"
          active={viewByUser}
          onClick={() => setViewByUser(!viewByUser)}
        />
        <FilterChip
          label="✅ Pflicht"
          active={filterOnlyRequired}
          onClick={() => setFilterOnlyRequired(!filterOnlyRequired)}
        />
        <FilterChip
          label="⏳ Offen"
          active={filterOnlyOpen}
          onClick={() => setFilterOnlyOpen(!filterOnlyOpen)}
        />
        {["before", "during", "after"].map((phase) => (
          <FilterChip
            key={phase}
            label={renderPhase(phase) ?? phase}
            active={filterPhase.includes(phase)}
            onClick={() =>
              setFilterPhase((prev) =>
                prev.includes(phase)
                  ? prev.filter((p) => p !== phase)
                  : [...prev, phase]
              )
            }
          />
        ))}
        <button
          onClick={() => {
            setViewByUser(false);
            setFilterOnlyOpen(false);
            setFilterOnlyRequired(false);
            setFilterPhase([]);
          }}
          className="ml-auto text-[11px] text-slate-600 hover:text-slate-400 transition-colors"
        >
          Zurücksetzen
        </button>
      </div>

      {/* States */}
      {loading && (
        <p className="text-slate-600 text-sm px-1">Lade Aufgaben…</p>
      )}
      {!loading && filteredTasks.length === 0 && (
        <div className="text-center py-10">
          <p className="text-slate-500 text-sm">Keine Aufgaben passen zum Filter.</p>
        </div>
      )}

      {/* By-user view */}
      {!loading && filteredTasks.length > 0 && viewByUser && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {tasksPerUser.map(([userId, userData]) => (
            <div
              key={userId}
              className="bg-[#161b27] rounded-xl border border-[#2a3347] p-4 hover:border-sky-500/20 transition-colors flex flex-col gap-2"
            >
              <h3 className="text-sm font-semibold text-slate-100">
                {userData.full_name}
              </h3>
              <div className="space-y-1.5 mt-1">
                {userData.items.map(({ task, done }) => (
                  <div
                    key={`${task.id}-${userId}`}
                    className="flex items-center gap-2"
                  >
                    <span className={`text-[10px] ${done ? "text-emerald-400" : "text-slate-600"}`}>
                      {done ? "✓" : "○"}
                    </span>
                    <span
                      className={`text-xs flex-1 ${
                        done ? "text-slate-500 line-through" : "text-slate-300"
                      }`}
                    >
                      {task.title}
                    </span>
                    {user?.id && isOrganizer(user.id) && (
                      <input
                        type="checkbox"
                        checked={done}
                        onChange={() => toggleDone(task.id, userId, !done)}
                        className="w-3.5 h-3.5 accent-sky-500 cursor-pointer"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* By-phase view */}
      {!loading && filteredTasks.length > 0 && !viewByUser && (
        <div className="space-y-6">
          {phaseOrder.map((phaseKey) => {
            const tasksInPhase = groupedTasks[phaseKey];
            if (!tasksInPhase || tasksInPhase.length === 0) return null;

            return (
              <div key={phaseKey}>
                {/* Phase divider */}
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[10px] uppercase tracking-widest text-slate-600">
                    {phaseLabels[phaseKey]}
                  </span>
                  <div className="flex-1 h-px bg-[#2a3347]" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tasksInPhase.map((task) => {
                    const doneCount = task.assigned_participants.filter(
                      (p) => p.done
                    ).length;
                    const totalCount = task.assigned_participants.length;
                    const allDone = doneCount === totalCount && totalCount > 0;

                    return (
                      <div
                        key={task.id}
                        className="bg-[#161b27] rounded-xl border border-[#2a3347] p-4 hover:border-sky-500/20 transition-colors flex flex-col gap-2"
                      >
                        {/* Title + badges */}
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="text-sm font-semibold text-slate-100 leading-snug flex-1">
                            {task.title}
                          </h4>
                          <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                allDone
                                  ? "bg-emerald-500/15 text-emerald-400"
                                  : "bg-[#1e2639] text-slate-500"
                              }`}
                            >
                              {doneCount}/{totalCount}
                            </span>
                            <span
                              className={`text-[10px] px-2 py-0.5 rounded-full ${
                                task.is_optional
                                  ? "bg-[#1e2639] text-slate-600"
                                  : "bg-amber-500/10 text-amber-500"
                              }`}
                            >
                              {task.is_optional ? "optional" : "Pflicht"}
                            </span>
                          </div>
                        </div>

                        {/* Description */}
                        {task.description && (
                          <p className="text-xs text-slate-400 leading-relaxed">
                            {task.description}
                          </p>
                        )}

                        {/* Due date */}
                        {task.due_date && (
                          <div className="text-[10px] text-slate-600">
                            Fällig:{" "}
                            {format(new Date(task.due_date), "PPP", {
                              locale: de,
                            })}
                          </div>
                        )}

                        {/* Participants */}
                        <div className="space-y-1.5 mt-1 pt-2 border-t border-[#1e2639]">
                          {task.assigned_participants.map((p) => (
                            <div
                              key={p.user_id}
                              className="flex items-center gap-2"
                            >
                              <span
                                className={`text-[10px] ${
                                  p.done ? "text-emerald-400" : "text-slate-600"
                                }`}
                              >
                                {p.done ? "✓" : "○"}
                              </span>
                              <span
                                className={`text-xs flex-1 ${
                                  p.done
                                    ? "text-slate-500 line-through"
                                    : "text-slate-300"
                                }`}
                              >
                                {p.full_name}
                              </span>
                              {user?.id && isOrganizer(user.id) && (
                                <input
                                  type="checkbox"
                                  checked={p.done}
                                  onChange={() =>
                                    toggleDone(task.id, p.user_id, !p.done)
                                  }
                                  className="w-3.5 h-3.5 accent-sky-500 cursor-pointer"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
